// Client wrappers for the Phase-2 Platform Admin callables:
// money ops (coupons, invoices, dunning, MRR) + the real ticket system.
// All are platform-admin guarded server-side.
import { call } from './callable';


// ── coupons ──────────────────────────────────────────────────────────────────
export interface Coupon {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  planIds: string[];
  expiresAt: number | null;
  maxRedemptions: number | null;
  redemptions: number;
  active: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface CouponInput {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  planIds?: string[];
  expiresAt?: number;
  maxRedemptions?: number;
  active: boolean;
}

const padminBillingC = call<any, any>('padminBilling');
const padminOpsC = call<any, any>('padminOps');

export const listCoupons = async () => (await padminBillingC({ action: 'listCoupons' })).data.coupons;
export const saveCoupon = async (c: CouponInput) => (await padminBillingC({ action: 'saveCoupon', ...c })).data.coupon;
export const deleteCoupon = async (code: string) => (await padminBillingC({ action: 'deleteCoupon', code })).data;

// ── plans ────────────────────────────────────────────────────────────────────
export const archivePlan = async (planId: string, archived: boolean) =>
  (await padminBillingC({ action: 'archivePlan', planId, archived })).data;

// ── invoices ─────────────────────────────────────────────────────────────────
export interface AdminInvoice {
  id: string;
  tenantId: string;
  tenantName: string;
  planId: string;
  cycle: 'monthly' | 'yearly';
  amountUsd: number;
  currency: string;
  status: 'pending' | 'paid' | 'failed';
  provider: string;
  providerRef: string | null;
  createdAt: number;
  paidAt: number | null;
  manual: boolean;
}

export const listInvoices = async (status?: string, limit?: number) =>
  (await padminBillingC({ action: 'listInvoices', status, limit })).data.invoices;
export const markInvoicePaid = async (p: { tenantId: string; invoiceId: string; reason: string }) =>
  (await padminBillingC({ action: 'markInvoicePaid', ...p })).data;

// ── revenue + dunning ────────────────────────────────────────────────────────
export interface PastDueRow {
  id: string; name: string; planId: string; status: string; renewalAt: number; daysLate: number;
}

export interface RevenueSummary {
  mrrUsd: number;
  arrUsd: number;
  tenants: number;
  paying: number;
  trials: number;
  suspended: number;
  conversionPct: number;
  pastDue: PastDueRow[];
}

export const revenueSummary = async () => (await padminBillingC({ action: 'revenueSummary' })).data;

// ── tickets ──────────────────────────────────────────────────────────────────
export type TicketStatus = 'open' | 'answered' | 'closed';
export type TicketPriority = 'low' | 'normal' | 'high';

export interface AdminTicketRow {
  id: string;
  tenantId: string;
  tenantName: string;
  subject: string;
  module: string;
  priority: TicketPriority;
  status: TicketStatus;
  openedByEmail: string | null;
  lastReplyBy: 'tenant' | 'staff';
  replyCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface TicketReply {
  id: string;
  body: string;
  authorUid: string;
  authorRole: 'tenant' | 'staff';
  authorEmail?: string | null;
  createdAt: number;
}

export const listTickets = async (status?: TicketStatus, limit?: number) =>
  (await padminOpsC({ action: 'listTickets', status, limit })).data.tickets;
export const getTicket = async (tenantId: string, ticketId: string) =>
  (await padminOpsC({ action: 'getTicket', tenantId, ticketId })).data;
export const replyTicket = async (p: { tenantId: string; ticketId: string; body: string; close?: boolean }) =>
  (await padminOpsC({ action: 'replyTicket', ...p, close: p.close ?? false })).data;
export const setTicketStatus = async (p: { tenantId: string; ticketId: string; status?: TicketStatus; priority?: TicketPriority }) =>
  (await padminOpsC({ action: 'setTicketStatus', ...p })).data;
